import React from "react";
import { Button, Grid } from "@material-ui/core";
import { FormProvider, useForm } from "react-hook-form";
import { CRadioGroup, CSelect, CTextField, HFlex, VFlex } from "shared";
import { GetUsersParams, UserRole, userRoles } from "./schema";

type FilterKey = "id" | "email" | "name" | "phone_number" | "uid";

const categories: { value: FilterKey; label: string }[] = [
  { value: "id", label: "ID" },
  { value: "email", label: "Email" },
  { value: "name", label: "Name" },
  { value: "phone_number", label: "Phone number" },
  { value: "uid", label: "UID" },
];

const roleLabels: Record<UserRole, string> = {
  patient: "Patient",
  therapist: "Therapist",
  clinic_partner: "Clinic partner",
  hcp_admin: "HCP admin",
  billing_admin: "Billing admin",
};

const ALL_ROLES = "all";
const roleItems: { value: UserRole | typeof ALL_ROLES; label: string }[] = [
  { value: ALL_ROLES, label: "All" },
  ...userRoles.map(r => ({ value: r, label: roleLabels[r] })),
];

interface Props {
  loading?: boolean;
  setFilters: (params: GetUsersParams) => void;
}

interface Fields {
  category: FilterKey;
  search: string;
  role: UserRole | typeof ALL_ROLES;
}

export const SearchUserForm: React.VFC<Props> = ({ loading, setFilters }) => {
  const form = useForm<Fields>({ defaultValues: { category: "id", search: "", role: ALL_ROLES } });
  const currentSearch = form.watch("search");
  const currentCategory = form.watch("category", "id");
  const selectedCategory = categories.find(c => c.value === currentCategory)!;

  const onSubmit = form.handleSubmit(({ category, search, role }) => {
    const params: GetUsersParams = { [category]: search };
    if (role !== ALL_ROLES) params.role = role;
    setFilters(params);
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit}>
        <VFlex gap={2}>
          <HFlex gap={2} alignItems="center">
            <CRadioGroup
              name="category"
              label="Search by"
              row
              items={categories}
              defaultValue="id"
              aria-label="Filter"
            />
            <CSelect name="role" label="Role" items={roleItems} defaultValue={ALL_ROLES}>
              {({ label }) => label}
            </CSelect>
          </HFlex>
          <Grid container alignItems="flex-start" spacing={2}>
            <Grid item xs={12} sm>
              <CTextField
                name="search"
                fullWidth
                required
                placeholder={selectedCategory.label}
                aria-label="Search string"
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={!currentSearch || loading}
                aria-label="Search"
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </VFlex>
      </form>
    </FormProvider>
  );
};
